import React from 'react';
import { Button, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { istDateTime } from '@/utils/cricketCalendar';
import { flagReasonParts, ruleHits } from '@/utils/pvpReview';
import type { PvpReviewPlayer, PvpReviewRule } from '@/types/pvp';

const { Text } = Typography;
const num = (n: number): string => n.toLocaleString('en-IN');
const small = { fontSize: 12 } as const;

interface Props {
  items: PvpReviewPlayer[];
  /** The rule in force, to mark the rates that meet it. */
  rule: PvpReviewRule;
  loading: boolean;
  canChange: boolean;
  deniedTitle?: string;
  busyIds: ReadonlySet<string>;
  onFlag: (player: PvpReviewPlayer) => void;
  onClear: (player: PvpReviewPlayer) => void;
}

/** A rate, in red when it meets the rule's threshold. */
const Rate: React.FC<{ value: number; label: string; hit: boolean; hitName: string }> = ({
  value,
  label,
  hit,
  hitName,
}) =>
  hit ? (
    <Text type="danger" strong data-hit={hitName}>
      {value} {label}
    </Text>
  ) : (
    <Text>
      {value} {label}
    </Text>
  );

/** Realtime PvP players to review: who, the flag, batting since the last clear and lifetime, record, conduct. */
export const ReviewTable: React.FC<Props> = ({
  items,
  rule,
  loading,
  canChange,
  deniedTitle,
  busyIds,
  onFlag,
  onClear,
}) => {
  const columns: ColumnsType<PvpReviewPlayer> = [
    {
      title: 'Player',
      key: 'player',
      render: (_, p) => (
        <div>
          <Link to={`/players/${p.userId}`}>{p.displayName ?? 'No name'}</Link>
          <div>
            <Text type="secondary" copyable={{ text: p.userId }} style={small}>
              {p.userId}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Flag',
      key: 'flag',
      render: (_, p) => {
        const reason = flagReasonParts(p.flagReason);
        if (!p.flaggedAt) return <Tag>Not flagged</Tag>;
        return (
          <div>
            <Tag color="red">Flagged</Tag>
            <Text style={small}>{istDateTime(p.flaggedAt)}</Text>
            {reason && (
              <div>
                <Tag color={reason.by === 'admin' ? 'purple' : 'orange'}>
                  {reason.by === 'admin' ? 'By admin' : 'By rule'}
                </Tag>
                {reason.text && <Text style={small}>{reason.text}</Text>}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <Tooltip title="What the rule judges. Clearing a flag restarts these from zero.">
          Since last clear
        </Tooltip>
      ),
      key: 'sinceClear',
      render: (_, p) => {
        const c = p.sinceClear;
        const hits = ruleHits(c, rule);
        return (
          <div>
            <Text>
              {num(c.balls)} balls · {num(c.runs)} runs · {num(c.sixes)} sixes
            </Text>
            <div>
              <Space size={8} split="·">
                <Rate
                  value={c.runsPerBall}
                  label="runs a ball"
                  hit={hits.runsPerBall}
                  hitName="runsPerBall"
                />
                <Rate value={c.sixRate} label="sixes a ball" hit={hits.sixRate} hitName="sixRate" />
              </Space>
            </div>
            {!hits.judged && (
              <Text type="secondary" style={small}>
                under {rule.minBalls} balls: not judged yet
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Lifetime batting',
      key: 'lifetime',
      render: (_, p) => (
        <Text>
          {num(p.ballsFaced)} balls · {num(p.runsScored)} runs · {num(p.sixes)} sixes
        </Text>
      ),
    },
    {
      title: 'Rating',
      key: 'rating',
      render: (_, p) => (
        <div>
          <Text>{num(p.rating)}</Text>
          <div>
            <Text type="secondary" style={small}>
              {p.wins}W {p.losses}L {p.draws}D
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="Late: ball reports that landed in the last 500 ms before the deadline. Rejected: payloads the server refused.">
          Reports
        </Tooltip>
      ),
      key: 'reports',
      render: (_, p) => (
        <Text style={small}>
          {num(p.lateReports)} late · {num(p.badPayloads)} rejected
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, p) => (
        <Tooltip title={deniedTitle}>
          {p.flaggedAt ? (
            <Button
              type="link"
              size="small"
              loading={busyIds.has(p.userId)}
              disabled={!canChange}
              onClick={() => onClear(p)}
            >
              Clear
            </Button>
          ) : (
            <Button
              type="link"
              size="small"
              danger
              loading={busyIds.has(p.userId)}
              disabled={!canChange}
              onClick={() => onFlag(p)}
            >
              Flag
            </Button>
          )}
        </Tooltip>
      ),
    },
  ];

  return (
    <Table<PvpReviewPlayer>
      size="small"
      rowKey="userId"
      loading={loading}
      columns={columns}
      dataSource={items}
      pagination={false}
      scroll={{ x: 'max-content' }}
      locale={{ emptyText: 'Nobody to review' }}
    />
  );
};
